import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, CalendarClock } from "lucide-react";
import { PlanPicker } from "./PlanPicker";
import { AddonConfigurator } from "./AddonConfigurator";
import { FractionalTeam } from "./FractionalTeam";
import { PlanSummary } from "./PlanSummary";
import { INSTALL_FEE, PLATFORM_MONTHLY, PER_SEAT_YR, HOURLY, INCLUDED, ADDONS, CADENCES, PLANS, ROLES, commitDiscount, fmt } from "./pricing";
export { INSTALL_FEE, PLATFORM_MONTHLY, HOURLY, INCLUDED, ADDONS, CADENCES, PLANS } from "./pricing";
import { submitRtdEngagement, shareRtdPlan, downloadRtdPlanPdf } from "@/lib/api";

const INK = "#0F1B2D";
const CYAN_DARK = "#0E509C";
const PINK_DARK = "#10346E";

const defaultKickoff = () => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10); };

export function EngagementBuilder({ engagementPct, sectionsViewed, onPrint }) {
  const [addons, setAddons] = useState(() => Object.fromEntries(ADDONS.map((a) => [a.key, { on: false, hours: a.defHours }])));
  const [cadence, setCadence] = useState("monthly");
  const [wishlist, setWishlist] = useState("");
  const [kickoff, setKickoff] = useState(defaultKickoff);
  const [showTeam, setShowTeam] = useState(false);
  const [roles, setRoles] = useState(() => Object.fromEntries(ROLES.map((r) => [r.key, { on: false, hours: r.def, mode: "committed" }])));
  const [seats, setSeats] = useState(40);
  const [form, setForm] = useState({ open: false, name: "", email: "", note: "", sending: false, sent: false });
  const [share, setShare] = useState({ open: false, name: "", busy: false, id: null, pdfBusy: false });

  const setRole = (key, patch) => setRoles((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  const setAddon = (key, patch) => setAddons((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const applyPlan = (plan) => {
    setAddons(Object.fromEntries(ADDONS.map((a) => [a.key, { on: plan.addons.includes(a.key), hours: a.defHours }])));
    setCadence("monthly");
    toast.success(`${plan.label} loaded — tweak anything below.`);
  };

  const addonLines = ADDONS.filter((a) => addons[a.key].on).map((a) => {
    const hours = addons[a.key].hours;
    return { ...a, hours, estCost: hours * HOURLY };
  });
  const addonsMonthly = addonLines.reduce((s, a) => s + a.monthly, 0);
  const addonsEstHours = addonLines.reduce((s, a) => s + a.estCost, 0);
  const cadenceObj = CADENCES.find((c) => c.key === cadence);

  const teamLines = ROLES.filter((r) => roles[r.key].on).map((r) => {
    const st = roles[r.key];
    const pct = r.discountable && st.mode === "committed" ? commitDiscount(st.hours) : 0;
    const effRate = r.rate * (1 - pct / 100);
    return { ...r, hours: st.hours, mode: st.mode, discount_pct: pct, effRate, monthly: Math.round(st.hours * effRate) };
  });
  const teamMonthly = teamLines.reduce((s, l) => s + l.monthly, 0);
  const trainingAnnual = seats * PER_SEAT_YR;
  const monthlyTotal = PLATFORM_MONTHLY + cadenceObj.monthly + addonsMonthly + teamMonthly;

  const activePlan = useMemo(() => {
    const on = ADDONS.filter((a) => addons[a.key].on).map((a) => a.key).sort().join(",");
    if (teamLines.length) return null;
    return PLANS.find((p) => [...p.addons].sort().join(",") === on) || null;
  }, [addons, teamLines.length]);
  const planKey = activePlan?.key || "custom";
  const planLabel = activePlan ? `${activePlan.label}${cadence !== "monthly" ? ` · ${cadenceObj.label.toLowerCase()} check-ins` : ""}` : "Custom plan";

  const planPayload = () => ({
    plan_key: planKey,
    plan_label: planLabel,
    install_fee: INSTALL_FEE,
    platform_monthly: PLATFORM_MONTHLY,
    wishlist: wishlist.trim() || null,
    cadence,
    cadence_monthly: cadenceObj.monthly,
    addons: addonLines.map((a) => ({ key: a.key, label: a.label, specialist: a.specialist, monthly: a.monthly, est_hours: a.hours, hourly_rate: HOURLY, est_hours_cost: a.estCost })),
    addons_monthly: addonsMonthly,
    addons_est_hours_monthly: addonsEstHours,
    team: teamLines.map((l) => ({ key: l.key, label: l.label, hours: l.hours, mode: l.mode, rate: l.rate, discount_pct: l.discount_pct, monthly: l.monthly })),
    team_monthly: teamMonthly,
    training_seats: seats,
    training_annual: trainingAnnual,
    monthly_total: monthlyTotal,
    est_variable_monthly: addonsEstHours,
    kickoff_date: kickoff || null,
  });

  const sendToColin = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required."); return; }
    setForm((f) => ({ ...f, sending: true }));
    try {
      const res = await submitRtdEngagement({ ...planPayload(), name: form.name.trim(), email: form.email.trim(), note: form.note.trim() || null, engagement_pct: engagementPct, sections_viewed: sectionsViewed });
      if (res?.ok) { setForm((f) => ({ ...f, sending: false, sent: true })); toast.success("Sent — Colin will follow up to schedule the demo."); }
      else { setForm((f) => ({ ...f, sending: false })); toast.error("Something went wrong — please try again."); }
    } catch (e) {
      setForm((f) => ({ ...f, sending: false }));
      toast.error("Network error — email colindarke@complisun.com directly.");
    }
  };

  const sharePlan = async () => {
    setShare((s) => ({ ...s, busy: true }));
    try {
      const res = await shareRtdPlan({ ...planPayload(), shared_by: share.name.trim() || null });
      if (res?.ok) { setShare((s) => ({ ...s, busy: false, id: res.id })); toast.success("Plan saved — share the link with your team."); }
      else { setShare((s) => ({ ...s, busy: false })); toast.error("Couldn't save the plan — try again."); }
    } catch (e) {
      setShare((s) => ({ ...s, busy: false }));
      toast.error("Network error — please try again.");
    }
  };
  const shareUrl = share.id ? `${window.location.origin}/p/rtd-financial/plan/${share.id}` : "";
  const copyLink = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied."); } catch { toast.error("Copy failed — select the link and copy manually."); } };
  const downloadPlanPdf = async () => {
    setShare((s) => ({ ...s, pdfBusy: true }));
    try { await downloadRtdPlanPdf(share.id); } catch { toast.error("Couldn't build the PDF."); }
    setShare((s) => ({ ...s, pdfBusy: false }));
  };

  return (
    <section id="build" className="anchor-target py-14 md:py-20" data-testid="engagement-builder">
      <span className="rt-eyebrow">Plan Builder · live pricing</span>
      <h2 className="rt-h text-4xl md:text-5xl mt-3 max-w-3xl">
        Pick the plan that makes sense. <span className="rt-grad-text">Share it with your team for approval.</span>
      </h2>
      <p className="mt-5 text-lg leading-relaxed max-w-3xl" style={{ color: INK, opacity: 0.78 }}>
        One flat platform fee covers everything an SEC-registered adviser needs — the whole list below, no
        per-module pricing. Add Vendor Management or Client Onboarding if you want named people on our team
        running them, choose how often we meet, and the price updates live. When it looks right, share a
        one-page summary with pricing and a go-live timeline for sign-off.
      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl" data-testid="savings-context">
        {[
          { key: "platform", label: "Compliance platform for an RIA", typical: "Typical RIA compliance SaaS", typicalPrice: "$2,500–$6,000/mo + $10–20K setup", ours: "$1,000/mo", oursNote: "flat · everything included" },
          { key: "cco", label: "Outsourced compliance consulting", typical: "Typical retainer", typicalPrice: "$8,000–$15,000/mo", ours: "$150/hr", oursNote: "only what you use · quoted first" },
        ].map((c) => (
          <div key={c.key} className="rt-card p-5" data-testid={`savings-${c.key}`}>
            <div className="rt-eyebrow mb-2">What this normally costs</div>
            <div className="rt-h text-[16px]" style={{ color: INK }}>{c.label}</div>
            <div className="mt-2 flex items-baseline justify-between gap-3 text-[13px]">
              <span style={{ color: INK, opacity: 0.6 }}>{c.typical}</span>
              <span className="line-through font-bold whitespace-nowrap" style={{ color: INK, opacity: 0.45 }}>{c.typicalPrice}</span>
            </div>
            <div className="mt-1.5 flex items-baseline justify-between gap-3">
              <span className="text-[13px] font-bold" style={{ color: PINK_DARK }}>CompliSun</span>
              <span className="rt-h text-xl whitespace-nowrap" style={{ color: CYAN_DARK }}>{c.ours}<span className="text-[12px] font-normal opacity-60"> {c.oursNote}</span></span>
            </div>
          </div>
        ))}
      </div>

      <PlanPicker planKey={planKey} onPick={applyPlan} />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5 items-start">
        <div className="space-y-4">
          {/* Base */}
          <div className="rt-card p-6">
            <div className="rt-eyebrow mb-4">The base · same in every plan</div>
            <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b" style={{ borderColor: "#E5EDF5" }}>
              <div>
                <div className="rt-h text-base" style={{ color: INK }}>Install / implementation</div>
                <div className="text-[12px]" style={{ color: INK, opacity: 0.6 }}>One-time · pay now or in installments · migration from your current vendor, configured to RTD's workflows</div>
              </div>
              <div className="rt-h text-xl" style={{ color: INK }} data-testid="setup-fee">{fmt(INSTALL_FEE)}<span className="text-sm opacity-55"> one-time</span></div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
              <div>
                <div className="rt-h text-base" style={{ color: INK }}>Platform fee — everything included</div>
                <div className="text-[12px] max-w-md" style={{ color: INK, opacity: 0.6 }}>No per-module pricing. Everything below, for every employee, plus your dedicated expert's monthly check-in. Starts only once you've transitioned off your current platform.</div>
              </div>
              <div className="rt-h text-xl" style={{ color: INK }} data-testid="platform-fee">{fmt(PLATFORM_MONTHLY)}<span className="text-sm opacity-55">/mo flat</span></div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="included-grid">
              {INCLUDED.map((m) => (
                <div key={m.label} className="flex items-start gap-2 rounded-xl px-3 py-2" style={{ background: "#F4F8FC" }}>
                  <Check className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: CYAN_DARK }} strokeWidth={3} />
                  <div>
                    <div className="text-[13px] font-bold leading-tight" style={{ color: INK }}>{m.label}</div>
                    <div className="text-[11px] leading-snug" style={{ color: INK, opacity: 0.6 }}>{m.sub}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label className="block text-[11px] font-extrabold uppercase tracking-[0.16em] mb-1.5" style={{ color: INK, opacity: 0.7 }}>
                Anything your current vendor never built for you? Add it to the prototype
              </label>
              <textarea value={wishlist} onChange={(e) => setWishlist(e.target.value)} rows={2} data-testid="module-wishlist"
                        placeholder="e.g. a 401(k) plan-committee tracker, quarterly holdings reports pulled from the custodian, a one-click SEC exam production index…"
                        className="w-full rounded-xl px-3.5 py-2.5 text-[13.5px] resize-none"
                        style={{ border: "1.5px solid #E5EDF5", color: INK, fontFamily: "inherit" }} />
            </div>
          </div>

          <AddonConfigurator addons={addons} setAddon={setAddon} />
          {/* Cadence */}
          <div className="rt-card p-6">
            <div className="rt-eyebrow mb-1 flex items-center gap-1.5"><CalendarClock className="h-3 w-3" /> Check-in cadence with your dedicated expert</div>
            <p className="text-[13px] mb-4" style={{ color: INK, opacity: 0.65 }}>
              A monthly one-hour meeting is included. Want to meet more often? Each extra meeting is one hour at ${HOURLY}/hr.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5" data-testid="cadence-picker">
              {CADENCES.map((c) => {
                const on = cadence === c.key;
                return (
                  <button key={c.key} type="button" onClick={() => setCadence(c.key)} data-testid={`cadence-${c.key}`} aria-pressed={on}
                          className="text-left rounded-2xl border p-3.5 transition-colors"
                          style={{ borderColor: on ? CYAN_DARK : "#E5EDF5", background: on ? CYAN_DARK : "#fff", color: on ? "#fff" : INK }}>
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="rt-h text-[15px]">{c.label}</div>
                      <div className="rt-h text-[13px]" data-testid={`cadence-price-${c.key}`}>{c.monthly ? `+${fmt(c.monthly)}/mo` : "included"}</div>
                    </div>
                    <div className="text-[11.5px] mt-0.5" style={{ opacity: 0.7 }}>{c.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Kickoff */}
          <div className="rt-card p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="rt-eyebrow mb-1">Target kickoff · drives the timeline you share</div>
              <div className="text-[13px]" style={{ color: INK, opacity: 0.65 }}>Prototype ~2 weeks after kickoff · fully live within 45 days · ahead of the 12/31–1/31 renewal window.</div>
            </div>
            <input type="date" value={kickoff} onChange={(e) => setKickoff(e.target.value)} data-testid="kickoff-date"
                   className="rounded-xl px-3.5 py-2.5 text-[14px] font-bold" style={{ border: "1.5px solid #E5EDF5", color: INK, fontFamily: "inherit" }} />
          </div>

          <FractionalTeam roles={roles} setRole={setRole} showTeam={showTeam} setShowTeam={setShowTeam} teamLines={teamLines} />
          {/* Training */}
          <div className="rt-card p-6">
            <div className="rt-eyebrow mb-1">Training seats</div>
            <p className="text-[13px] mb-3" style={{ color: INK, opacity: 0.65 }}>
              The full catalog plus RTD-specific modules — ${PER_SEAT_YR}/seat/year, no setup fee. Billed annually, separate from the platform.
            </p>
            <input type="range" min={10} max={300} step={5} value={seats} onChange={(e) => setSeats(Number(e.target.value))} data-testid="builder-seats-slider" className="w-full accent-[#0E509C]" />
            <div className="flex justify-between items-baseline mt-1">
              <div className="text-[13px] font-bold" style={{ color: INK }}>{seats} seats</div>
              <div className="rt-h text-lg" style={{ color: CYAN_DARK }} data-testid="builder-training-annual">{fmt(trainingAnnual)}/yr</div>
            </div>
          </div>
        </div>

        <PlanSummary planLabel={planLabel} cadenceObj={cadenceObj} addonLines={addonLines} teamLines={teamLines} seats={seats}
                     trainingAnnual={trainingAnnual} monthlyTotal={monthlyTotal} addonsEstHours={addonsEstHours}
                     form={form} setForm={setForm} share={share} setShare={setShare} shareUrl={shareUrl} onPrint={onPrint}
                     sendToColin={sendToColin} sharePlan={sharePlan} copyLink={copyLink} downloadPlanPdf={downloadPlanPdf} />
      </div>
    </section>
  );
}
